import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Stack,
  FormControlLabel,
  Switch,
  IconButton,
  Popover,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Search, Settings } from "@mui/icons-material";
import { MerchantWithStats } from "../services/statsService";
import { useMerchantsForMap } from "src/queries/stats";

declare global {
  interface Window {
    mapboxgl: any;
  }
}

export interface MapMerchant {
  id: number;
  name: string;
  lat: number;
  lng: number;
  totalStamps: number;
  subscriptionStatus: string | null;
}

type TimePeriod = "daily" | "weekly" | "monthly" | "yearly";

interface ColorRange {
  min: number;
  max: number | null;
  color: string;
  label: string;
}

interface MerchantMapProps {
  accessToken: string;
  height?: number | string;
}

const DEFAULT_COLOR_RANGES: ColorRange[] = [
  { min: 0, max: 0, color: "#D32F2F", label: "0" },
  { min: 1, max: 10, color: "#F57C00", label: "1-10" },
  { min: 11, max: 50, color: "#FBC02D", label: "11-50" },
  { min: 51, max: 100, color: "#7CB342", label: "51-100" },
  { min: 101, max: null, color: "#388E3C", label: "100+" },
];

const MerchantMap: React.FC<MerchantMapProps> = ({
  accessToken,
  height = 500,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  // `error` covers Mapbox script/init failures; the data error comes from the query.
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("daily");
  const [showActive, setShowActive] = useState(true);
  const [showFreeTrial, setShowFreeTrial] = useState(true);

  const [colorRanges, setColorRanges] = useState<ColorRange[]>(DEFAULT_COLOR_RANGES);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [tempRanges, setTempRanges] = useState<ColorRange[]>(DEFAULT_COLOR_RANGES);

  const getColorForStamps = useCallback((stamps: number): string => {
    for (const range of colorRanges) {
      if (range.max === null) {
        if (stamps >= range.min) return range.color;
      } else {
        if (stamps >= range.min && stamps <= range.max) return range.color;
      }
    }
    return "#9E9E9E";
  }, [colorRanges]);

  // Keyed on the period, so toggling back to a period already viewed is instant.
  // This endpoint was one the old Map cache could not cache at all.
  const {
    data: merchants = [] as MerchantWithStats[],
    isFetching: isLoadingData,
    isError: hasDataError,
  } = useMerchantsForMap(timePeriod);

  const displayError =
    error ?? (hasDataError ? "Failed to load merchant data" : null);

  const filteredMerchants = useMemo(() => {
    return merchants.filter((m) => {
      if (!m.lat || !m.lng) return false;
      const isActive = m.subscriptionStatus === "ACTIVE";
      const isFreeTrial = m.subscriptionStatus === "FREE_TRIAL";
      if (!showActive && !showFreeTrial) return false;
      if (showActive && isActive) return true;
      if (showFreeTrial && isFreeTrial) return true;
      return false;
    });
  }, [merchants, showActive, showFreeTrial]);

  const displayedMerchants = useMemo(() => {
    return filteredMerchants.filter(
      (m) => searchTerm === "" || fuzzyMatch(m.name, searchTerm)
    );
  }, [filteredMerchants, searchTerm]);

  useEffect(() => {
    if (window.mapboxgl) {
      setIsLoaded(true);
      return;
    }
    const cssLink = document.createElement("link");
    cssLink.href = "https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css";
    cssLink.rel = "stylesheet";
    document.head.appendChild(cssLink);

    const script = document.createElement("script");
    script.src = "https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js";
    script.async = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () =>
      setError("Failed to load Mapbox. Please check your internet connection.");
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapContainer.current || !accessToken) return;
    if (map.current) return;

    try {
      window.mapboxgl.accessToken = accessToken;
      const center: [number, number] = [18.9, -33.9];
      const zoom = 6;

      map.current = new window.mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/light-v11",
        center,
        zoom,
      });

      map.current.addControl(new window.mapboxgl.NavigationControl(), "top-right");
      map.current.addControl(new window.mapboxgl.FullscreenControl(), "top-right");
    } catch (err) {
      console.error("Error initializing map:", err);
      setError("Failed to initialize map. Please check your Mapbox access token.");
    }
  }, [isLoaded, accessToken]);

  useEffect(() => {
    if (!map.current || !isLoaded) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    displayedMerchants.forEach((merchant) => {
      const el = document.createElement("div");
      const markerColor = getColorForStamps(merchant.totalStamps);
      const statusColor = getStatusColor(merchant.subscriptionStatus);
      const stampText = formatStamps(merchant.totalStamps);

      el.style.cssText = `
        background-color: ${markerColor};
        width: 36px;
        height: 36px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 600;
        font-size: 13px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;

      el.innerText = stampText;

      const popup = new window.mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div style="padding: 8px; min-width: 150px;">
          <strong style="font-size: 14px;">${merchant.name}</strong>
          <div style="margin-top: 8px; font-size: 12px; color: #666;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span>Stamps (${timePeriod}):</span>
              <strong style="color: ${markerColor};">${merchant.totalStamps.toLocaleString()}</strong>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Status:</span>
              <span style="
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 10px;
                background-color: ${statusColor};
                color: white;
              ">${merchant.subscriptionStatus || "N/A"}</span>
            </div>
          </div>
        </div>
      `);

      const marker = new window.mapboxgl.Marker(el)
        .setLngLat([merchant.lng, merchant.lat])
        .setPopup(popup)
        .addTo(map.current);

      markersRef.current.push(marker);
    });
  }, [displayedMerchants, isLoaded, timePeriod, getColorForStamps]);

  useEffect(() => {
    if (!map.current || !searchTerm || displayedMerchants.length === 0) return;

    if (displayedMerchants.length === 1) {
      map.current.flyTo({
        center: [displayedMerchants[0].lng, displayedMerchants[0].lat],
        zoom: 14,
        duration: 1500,
      });
    } else if (displayedMerchants.length > 1) {
      const bounds = new window.mapboxgl.LngLatBounds();
      displayedMerchants.forEach((m) => bounds.extend([m.lng, m.lat]));
      map.current.fitBounds(bounds, { padding: 50, duration: 1500 });
    }
  }, [searchTerm, displayedMerchants]);

  const handleOpenSettings = (event: React.MouseEvent<HTMLElement>) => {
    setTempRanges([...colorRanges]);
    setSettingsAnchor(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setSettingsAnchor(null);
  };

  const handleSaveSettings = () => {
    setColorRanges([...tempRanges]);
    setSettingsAnchor(null);
  };

  const handleResetSettings = () => {
    setTempRanges([...DEFAULT_COLOR_RANGES]);
  };

  const handleRangeChange = (index: number, field: "min" | "max", value: string) => {
    const newRanges = [...tempRanges];
    const numValue = value === "" ? 0 : Math.max(0, parseInt(value, 10) || 0);

    if (field === "max" && value === "" && index === newRanges.length - 1) {
      // Only the last range can have null (infinity) max
      newRanges[index] = { ...newRanges[index], max: null };
    } else if (field === "max") {
      // Ensure max is at least equal to min
      const minVal = newRanges[index].min;
      const clampedMax = Math.max(minVal, numValue);
      newRanges[index] = { ...newRanges[index], max: clampedMax };
      
      // Cascade: adjust next range's min to be max + 1
      if (index < newRanges.length - 1) {
        newRanges[index + 1] = { ...newRanges[index + 1], min: clampedMax + 1 };
        // Also ensure next range's max is valid
        if (newRanges[index + 1].max !== null && newRanges[index + 1].max! < newRanges[index + 1].min) {
          newRanges[index + 1].max = newRanges[index + 1].min;
        }
      }
    } else if (field === "min") {
      // Ensure min doesn't exceed max (unless max is null)
      let clampedMin = numValue;
      if (newRanges[index].max !== null) {
        clampedMin = Math.min(numValue, newRanges[index].max!);
      }
      
      // Ensure min is greater than previous range's max
      if (index > 0 && newRanges[index - 1].max !== null) {
        clampedMin = Math.max(clampedMin, newRanges[index - 1].max! + 1);
      }
      
      newRanges[index] = { ...newRanges[index], min: clampedMin };
      
      // Cascade: adjust previous range's max to be min - 1
      if (index > 0 && clampedMin > 0) {
        const newPrevMax = clampedMin - 1;
        newRanges[index - 1] = { ...newRanges[index - 1], max: newPrevMax };
        // Also ensure previous range's min is valid
        if (newRanges[index - 1].min > newPrevMax) {
          newRanges[index - 1].min = newPrevMax;
        }
      }
    }

    // Update all labels
    newRanges.forEach((range, i) => {
      if (range.max === null) {
        range.label = `${range.min}+`;
      } else if (range.min === range.max) {
        range.label = `${range.min}`;
      } else {
        range.label = `${range.min}-${range.max}`;
      }
    });

    setTempRanges(newRanges);
  };

  if (displayError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {displayError}
      </Alert>
    );
  }

  if (!accessToken) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Mapbox access token not configured. Add REACT_APP_MAPBOX_ACCESS_TOKEN to
        your environment variables.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Controls outside the map */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              label="Time Period"
              onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
            >
              <MenuItem value="daily">Today</MenuItem>
              <MenuItem value="weekly">This Week</MenuItem>
              <MenuItem value="monthly">This Month</MenuItem>
              <MenuItem value="yearly">This Year</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search merchants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={showActive}
                onChange={(e) => setShowActive(e.target.checked)}
                size="small"
              />
            }
            label="Active"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showFreeTrial}
                onChange={(e) => setShowFreeTrial(e.target.checked)}
                size="small"
              />
            }
            label="Free Trial"
          />
        </Stack>
      </Box>

      {/* Map container */}
      <Box
        ref={mapContainer}
        sx={{
          width: "100%",
          height,
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          bgcolor: "#f5f5f5",
        }}
      >
        {!isLoaded && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              zIndex: 10,
              bgcolor: "rgba(255,255,255,0.8)",
              p: 3,
              borderRadius: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading map...
            </Typography>
          </Box>
        )}
        {isLoaded && isLoadingData && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      {/* Legend and settings below map */}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          Showing {displayedMerchants.length} merchants
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {colorRanges.map((range, index) => (
          <Stack key={index} direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: range.color,
              }}
            />
            <Typography variant="caption">{range.label}</Typography>
          </Stack>
        ))}
        <Typography variant="caption" color="text.secondary">
          stamps
        </Typography>
        <IconButton
          size="small"
          onClick={handleOpenSettings}
          title="Configure color ranges"
        >
          <Settings fontSize="small" />
        </IconButton>
      </Stack>

      {/* Settings Popover */}
      <Popover
        open={Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={handleCloseSettings}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Color Range Settings
          </Typography>

          {tempRanges.map((range, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: range.color,
                  flexShrink: 0,
                }}
              />
              <TextField
                size="small"
                type="number"
                label="Min"
                value={range.min}
                onChange={(e) => handleRangeChange(index, "min", e.target.value)}
                sx={{ width: 80 }}
                inputProps={{ min: 0 }}
              />
              <Typography variant="body2">-</Typography>
              <TextField
                size="small"
                type="number"
                label="Max"
                value={range.max === null ? "" : range.max}
                onChange={(e) => handleRangeChange(index, "max", e.target.value)}
                sx={{ width: 80 }}
                placeholder="∞"
                inputProps={{ min: 0 }}
              />
            </Stack>
          ))}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="small" onClick={handleResetSettings}>
              Reset
            </Button>
            <Button size="small" variant="contained" onClick={handleSaveSettings}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

function getStatusColor(status: string | null): string {
  switch (status) {
    case "ACTIVE":
      return "#4CAF50";
    case "FREE_TRIAL":
      return "#2196F3";
    default:
      return "#9E9E9E";
  }
}

function formatStamps(num: number): string {
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  return num.toString();
}

function fuzzyMatch(text: string, search: string): boolean {
  const searchLower = search.toLowerCase();
  const textLower = text.toLowerCase();

  if (textLower.includes(searchLower)) return true;

  let searchIndex = 0;
  for (let i = 0; i < textLower.length && searchIndex < searchLower.length; i++) {
    if (textLower[i] === searchLower[searchIndex]) {
      searchIndex++;
    }
  }
  if (searchIndex === searchLower.length) return true;

  if (searchLower.length > 3) {
    const words = textLower.split(/\s+/);
    for (const word of words) {
      if (levenshteinDistance(word, searchLower) <= 1) return true;
      if (word.length >= searchLower.length) {
        const wordStart = word.substring(0, searchLower.length);
        if (levenshteinDistance(wordStart, searchLower) <= 1) return true;
      }
    }
  }

  return false;
}

function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b[i - 1] === a[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

export default MerchantMap;
